import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AuthUser } from '../types';
import {
  clearSession,
  fetchCurrentUser,
  googleSignInRequest,
  loginRequest,
  logoutRequest,
  phoneSignInRequest,
  registerRequest,
} from '../api/auth';
import { ApiError } from '../api/client';
import { analyticsEvents, signInWithGoogle } from '../lib/firebase';

type AuthStatus = 'loading' | 'authenticated' | 'guest';

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (name: string, email: string, password: string) => Promise<AuthUser>;
  loginWithGoogle: () => Promise<AuthUser>;
  loginWithPhone: (idToken: string, name?: string) => Promise<AuthUser>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // Always start loading — session may live only in the shared SSO cookie.
  const [status, setStatus] = useState<AuthStatus>('loading');

  const refreshSession = useCallback(async () => {
    try {
      const me = await fetchCurrentUser();
      setUser(me);
      setStatus('authenticated');
      return me;
    } catch (err) {
      // Only clear the session on definitive auth failures — network/5xx blips
      // must not kick an otherwise valid student to guest mid-session.
      const statusCode = err instanceof ApiError ? err.status : 0;
      if (statusCode === 401 || statusCode === 403) {
        clearSession();
        setUser(null);
        setStatus('guest');
      }
      return null;
    }
  }, []);

  // On startup, verify Bearer token and/or shared cookie via /auth/me.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchCurrentUser();
        if (cancelled) return;
        setUser(me);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        clearSession();
        setUser(null);
        setStatus('guest');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh role/identity when the tab regains focus and periodically so Admin
  // promotions (student → faculty) apply without a full logout.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    const onFocus = () => { void refreshSession(); };
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => { void refreshSession(); }, 5 * 60 * 1000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [status, refreshSession]);

  const login = useCallback(async (email: string, password: string) => {
    const me = await loginRequest(email, password);
    setUser(me);
    setStatus('authenticated');
    analyticsEvents.login('email');
    return me;
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const me = await registerRequest(name, email, password);
    setUser(me);
    setStatus('authenticated');
    analyticsEvents.signUp('email');
    return me;
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const idToken = await signInWithGoogle();
    const me = await googleSignInRequest(idToken);
    setUser(me);
    setStatus('authenticated');
    analyticsEvents.login('google');
    return me;
  }, []);

  const loginWithPhone = useCallback(async (idToken: string, name?: string) => {
    const me = await phoneSignInRequest(idToken, name);
    setUser(me);
    setStatus('authenticated');
    analyticsEvents.login('phone');
    return me;
  }, []);

  const logout = useCallback(() => {
    void logoutRequest();
    clearSession();
    setUser(null);
    setStatus('guest');
    analyticsEvents.logout();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAuthenticated: status === 'authenticated',
      login,
      register,
      loginWithGoogle,
      loginWithPhone,
      logout,
    }),
    [user, status, login, register, loginWithGoogle, loginWithPhone, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
